import { useEffect, useState, useRef } from "react";
import { useGetTalentProfile } from "./useGetTalentProfile";
import { talentProfileStore } from "../components/talentProfile/TalentProfileStore";

export const useProfileCompletion = () => {
  const {
    data: profileData,
    isLoading: isProfileLoading,
    refetch,
  } = useGetTalentProfile();
  const [incompleteSections, setIncompleteSections] = useState<string[]>([]);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const lastProfileDataRef = useRef<string>("");

  const isEducationEntryComplete = (edu: any): boolean => {
    return !!(
      edu.school &&
      edu.school.trim().length > 0 &&
      edu.course &&
      edu.course.trim().length > 0 &&
      edu.degree_id &&
      edu.grade &&
      edu.start_date &&
      (edu.is_current || (edu.end_date && edu.end_date !== "1970-01-01"))
    );
  };

  const isWorkEntryComplete = (work: any): boolean => {
    const hasDescription =
      work.description && work.description.trim().length > 0;
    return !!(
      work.company &&
      work.company.trim().length > 0 &&
      work.title &&
      work.title.trim().length > 0 &&
      work.sector_id &&
      work.start_date &&
      (work.is_current === 1 ||
        (work.end_date && work.end_date !== "1970-01-01")) &&
      hasDescription
    );
  };

  const isVolunteerEntryComplete = (volunteer: any): boolean => {
    const hasValidEndDate =
      volunteer.is_current === 1 ||
      (volunteer.end_date &&
        volunteer.end_date !== "1970-01-01" &&
        volunteer.end_date !== null);

    const isComplete = !!(
      volunteer.company &&
      volunteer.company.trim().length > 0 &&
      volunteer.title &&
      volunteer.title.trim().length > 0 &&
      volunteer.start_date &&
      hasValidEndDate
    );

    return isComplete;
  };

  const calculateAndUpdateStatus = (profile: any) => {
    if (!profile) return;

    const educationHistories = profile.education_histories || [];

    const isEducationComplete =
      educationHistories.length > 0 &&
      educationHistories.every((edu: any) => isEducationEntryComplete(edu));

    const allWorkHistories = profile.work_histories || [];
    const workHistories = allWorkHistories.filter((work: any) => {
      return work.sector_id !== undefined && work.sector_id !== null;
    });

    const volunteerHistoriesFromWork = allWorkHistories.filter((work: any) => {
      return (
        !work.sector_id ||
        work.sector_id === null ||
        work.sector_id === "" ||
        (work.type && work.type.toLowerCase() === "volunteer")
      );
    });

    const volunteerHistoriesFromVolunteer = profile.volunteer_experiences || [];

    const volunteerHistories = [
      ...volunteerHistoriesFromWork,
      ...volunteerHistoriesFromVolunteer,
    ];

    const allWorkComplete =
      workHistories.length > 0 &&
      workHistories.every((work: any) => isWorkEntryComplete(work));

    const allVolunteerComplete =
      volunteerHistories.length > 0 &&
      volunteerHistories.every((volunteer: any) =>
        isVolunteerEntryComplete(volunteer),
      );

    const isExperienceComplete = allWorkComplete || allVolunteerComplete;

    const isBasicDetailsComplete = !!(
      profile.first_name &&
      profile.last_name &&
      profile.email &&
      profile.gender &&
      profile.executive_summary &&
      profile.professional_goal &&
      profile.phone &&
      profile.country &&
      profile.city &&
      profile.experience_level &&
      profile.current_position &&
      profile.sector_id &&
      profile.skills?.length > 0
    );

    const status = {
      "Basic Details": isBasicDetailsComplete,
      Education: isEducationComplete,
      Experience: isExperienceComplete,
    };

    talentProfileStore.setStatus(status);

    const incomplete: string[] = [];
    if (!status["Basic Details"]) incomplete.push("Basic Details");
    if (!status.Education) incomplete.push("Education");
    if (!status.Experience) incomplete.push("Experience");

    setIncompleteSections(incomplete);
    setIsProfileComplete(incomplete.length === 0);
  };

  useEffect(() => {
    if (profileData?.data) {
      const profileString = JSON.stringify(profileData.data);
      if (lastProfileDataRef.current !== profileString) {
        lastProfileDataRef.current = profileString;
        calculateAndUpdateStatus(profileData.data);
      }
    }
  }, [profileData]);

  return {
    incompleteSections,
    isProfileComplete,
    isLoading: isProfileLoading,
    refetchProfile: refetch,
  };
};
